import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useFinancasStore } from '@/stores/financas.store'
import { mockMinisterios } from '@/mocks'
import { formatCurrency, slugify } from '@/lib/utils'
import 'jspdf-autotable'
import { useAuth } from '@/hooks/use-auth-wrapper'

interface SaldoEnviado {
  id: string
  ministerioId: string
  valor: number
  data: string
}

export function FinancasMinisterio({ ministerioNome }: { ministerioNome: string }) {
  // Todos os hooks devem vir primeiro, antes de qualquer early return
  const { prestacoes, saldoMinisterios, solicitacoes, entradasMinisterios } = useFinancasStore()
  const [filtros, setFiltros] = useState<Filtros>({
    dataInicial: '',
    dataFinal: '',
    tipo: '',
    descricao: '',
  })
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false)
  const [modal, setModal] = useState<'solicitacao' | 'entrada' | 'prestacao' | 'transferir' | null>(
    null
  )

  // Buscar ministério pelo nome (convertendo o slug de volta para comparação)
  const ministerio = mockMinisterios.find((m) => slugify(m.nome) === ministerioNome)
  
  if (!ministerio) {
    return <div>Ministério não encontrado</div>
  }
  
  const ministerioId = ministerio.id
  
  // Buscar saldos enviados do localStorage
  const saldosEnviados = JSON.parse(localStorage.getItem('saldosEnviados') || '[]')
  const saldosDoMinisterio = saldosEnviados
    .filter((saldo: SaldoEnviado) => saldo.ministerioId === ministerioId)
    .reduce((total: number, saldo: SaldoEnviado) => total + saldo.valor, 0)
  
  // Saldo base + saldos enviados
  const saldoBase = saldoMinisterios.find((s) => s.ministerioId === ministerioId)?.saldoDisponivel || 0
  const saldo = saldoBase + saldosDoMinisterio

  // Função para verificar se uma data está no mês atual
  const isCurrentMonth = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()
  }

  // Calcular entradas do mês atual (entradas + solicitações aprovadas)
  const entradasDiretasMesAtual = entradasMinisterios
    .filter((entrada) => entrada.ministerioId === ministerioId && isCurrentMonth(entrada.data))
    .reduce((total, entrada) => total + entrada.valor, 0)

  const solicitacoesAprovadasMesAtual = solicitacoes
    .filter((solicitacao) => 
      solicitacao.ministerioId === ministerioId && 
      solicitacao.status === 'aprovado' && 
      isCurrentMonth(solicitacao.data)
    )
    .reduce((total, solicitacao) => total + solicitacao.valor, 0)

  const entradasMesAtual = entradasDiretasMesAtual + solicitacoesAprovadasMesAtual

  // Calcular saídas do mês atual (prestações + transferências)
  const prestacoesMesAtual = prestacoes
    .filter((prestacao) => prestacao.ministerioId === ministerioId && isCurrentMonth(prestacao.data))
    .reduce((total, prestacao) => total + prestacao.valor, 0)

  // Transferências são as solicitações aprovadas de transferência
  const transferenciasEnviadasMesAtual = solicitacoes
    .filter((solicitacao) => 
      solicitacao.ministerioId === ministerioId && 
      solicitacao.status === 'aprovado' && 
      solicitacao.titulo.toLowerCase().includes('transferência') &&
      isCurrentMonth(solicitacao.data)
    )
    .reduce((total, solicitacao) => total + solicitacao.valor, 0)

  const saidasMesAtual = prestacoesMesAtual + transferenciasEnviadasMesAtual

  // Estado dos filtros
  interface Filtros {
    dataInicial: string
    dataFinal: string
    descricao: string
    tipo: string
  }

  // Combinar todos os registros (prestações, entradas, solicitações, transferências, saldos enviados)
  const todosRegistros = [
    ...prestacoes
      .filter((p) => p.ministerioId === ministerioId)
      .map((p) => ({
        id: p.id,
        data: p.data,
        tipo: 'prestacao' as const,
        descricao: p.titulo,
        valor: p.valor,
        status: 'aprovado' as const,
      })),
    ...entradasMinisterios
      .filter((e) => e.ministerioId === ministerioId)
      .map((e) => ({
        id: e.id,
        data: e.data,
        tipo: 'entrada' as const,
        descricao: `${e.tipo.charAt(0).toUpperCase() + e.tipo.slice(1)} - ${e.observacoes || 'Entrada no ministério'}`,
        valor: e.valor,
        status: 'aprovado' as const,
      })),
    ...solicitacoes
      .filter((s) => s.ministerioId === ministerioId)
      .map((s) => ({
        id: s.id,
        data: s.data,
        tipo: s.titulo.toLowerCase().includes('transferência')
          ? 'transferencia'
          : ('solicitacao' as const),
        descricao: s.titulo,
        valor: s.valor,
        status: s.status,
      })),
    // Adicionar saldos enviados como registros de entrada// Saldos enviados
    ...saldosEnviados
      .filter((saldo: SaldoEnviado) => saldo.ministerioId === ministerioId)
      .map((saldo: SaldoEnviado) => ({
        id: saldo.id,
        data: saldo.data.split('T')[0], // Converter ISO para formato de data
        tipo: 'saldo_enviado' as const,
        descricao: 'Saldo enviado pela administração',
        valor: saldo.valor,
        status: 'aprovado' as const,
      })),
  ]

  // Filtrar registros
  const registrosFiltrados = todosRegistros.filter((registro) => {
    const matchData =
      (!filtros.dataInicial || registro.data >= filtros.dataInicial) &&
      (!filtros.dataFinal || registro.data <= filtros.dataFinal)
    const matchDescricao =
      !filtros.descricao ||
      registro.descricao.toLowerCase().includes(filtros.descricao.toLowerCase())
    const matchTipo = !filtros.tipo || registro.tipo === filtros.tipo

    return matchData && matchDescricao && matchTipo
  })

  // Função para gerar relatório
  const gerarRelatorio = () => {
    setGerandoRelatorio(true)
    import('jspdf')
      .then(({ default: jsPDF }) => {
        const doc = new jsPDF()

        // Título
        doc.setFontSize(20)
        doc.text(`Relatório Financeiro - ${ministerio?.nome}`, 20, 20)

        // Subtítulo com filtros aplicados
        doc.setFontSize(12)
        let y = 40
        if (filtros.dataInicial || filtros.dataFinal) {
          doc.text(
            `Período: ${filtros.dataInicial || 'início'} até ${filtros.dataFinal || 'atual'}`,
            20,
            y
          )
          y += 10
        }
        if (filtros.tipo) {
          doc.text(`Tipo: ${filtros.tipo}`, 20, y)
          y += 10
        }
        if (filtros.descricao) {
          doc.text(`Busca: ${filtros.descricao}`, 20, y)
          y += 10
        }

        // Tabela
        y += 10
        doc.setFontSize(10)
        const headers = ['Data', 'Tipo', 'Descrição', 'Valor', 'Status']
        const data = registrosFiltrados.map((r) => [
          new Date(r.data).toLocaleDateString('pt-BR'),
          r.tipo,
          r.descricao,
          formatCurrency(r.valor),
          r.status,
        ])

        // Auto-table
        import('jspdf-autotable').then((ATPkg) => {
          const autoTable = ATPkg.default || ATPkg
          autoTable(doc, {
            head: [headers],
            body: data,
            startY: y,
          })

          // Download
          doc.save(
            `relatorio-${ministerio?.nome.toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`
          )
        })
      })
      .catch((error) => {
        console.error('Erro ao gerar PDF:', error)
        // TODO: Adicionar um toast de erro
      })
      .finally(() => {
        setGerandoRelatorio(false)
      })
  }

  if (!ministerio) {
    return <div>Ministério não encontrado</div>
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho com saldo */}
      <Card className="bg-card dark:bg-gray-800 border-border dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-card-foreground dark:text-gray-400">Finanças - {ministerio.nome}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="text-sm text-muted-foreground">{ministerio.descricao}</div>
            
            {/* Grid com informações financeiras */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Saldo Disponível */}
              <div className="flex flex-col items-center p-4 bg-card dark:bg-gray-800 border-border dark:border-gray-700 rounded-lg border">
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Saldo Disponível</span>
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">{formatCurrency(saldo)}</span>
              </div>
              
              {/* Entradas do Mês */}
              <div className="flex flex-col items-center p-4 bg-card dark:bg-gray-800 border-border dark:border-gray-700 rounded-lg border">
                <span className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">Entradas do Mês</span>
                <span className="text-2xl font-bold text-green-700 dark:text-green-300">+{formatCurrency(entradasMesAtual)}</span>
              </div>
              
              {/* Saídas do Mês */}
              <div className="flex flex-col items-center p-4 bg-card dark:bg-gray-800 border-border dark:border-gray-700 rounded-lg border">
                <span className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Saídas do Mês</span>
                <span className="text-2xl font-bold text-red-700 dark:text-red-300">-{formatCurrency(saidasMesAtual)}</span>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex flex-wrap gap-3 mt-4">
              <Button 
                onClick={() => setModal('solicitacao')}
                className="bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white"
              >
                Solicitar Verba
              </Button>
              <Button 
                onClick={() => setModal('entrada')}
                className="bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white"
              >
                Entradas
              </Button>
              <Button 
                onClick={() => setModal('prestacao')}
                className="bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white"
              >
                Prestação de Contas
              </Button>
              <Button 
                onClick={() => setModal('transferir')}
                className="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white"
              >
                Transferir
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Registros */}
      <Card className="bg-card dark:bg-gray-800 border-border dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-card-foreground dark:text-gray-400">Registros Financeiros</CardTitle>
            <Button variant="outline" onClick={gerarRelatorio} disabled={gerandoRelatorio}>
              {gerandoRelatorio ? 'Gerando...' : 'Gerar Relatório'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="mb-6 grid gap-4 md:grid-cols-4">
            <div>
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={filtros.dataInicial}
                onChange={(e) => setFiltros((prev) => ({ ...prev, dataInicial: e.target.value }))}
              />
            </div>
            <div>
              <Label>Data Final</Label>
              <Input
                type="date"
                value={filtros.dataFinal}
                onChange={(e) => setFiltros((prev) => ({ ...prev, dataFinal: e.target.value }))}
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input
                placeholder="Buscar por descrição..."
                value={filtros.descricao}
                onChange={(e) => setFiltros((prev) => ({ ...prev, descricao: e.target.value }))}
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select
                value={filtros.tipo}
                onValueChange={(v) => setFiltros((prev) => ({ ...prev, tipo: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="solicitacao">Solicitação</SelectItem>
                  <SelectItem value="entrada">Entrada</SelectItem>
                  <SelectItem value="prestacao">Prestação</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="saldo_enviado">Saldo Enviado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabela */}
          <div className="relative overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="px-6 py-3">Data</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="px-6 py-3">Descrição</th>
                  <th className="px-6 py-3 text-right">Valor</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {registrosFiltrados.map((registro) => (
                  <tr key={registro.id} className="border-b">
                    <td className="px-6 py-4">
                      {new Date(registro.data).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      {registro.tipo === 'saldo_enviado' 
                        ? 'Saldo Enviado' 
                        : registro.tipo === 'entrada'
                        ? 'Entrada'
                        : registro.tipo.charAt(0).toUpperCase() + registro.tipo.slice(1)}
                    </td>
                    <td className="px-6 py-4">{registro.descricao}</td>
                    <td className="px-6 py-4 text-right font-medium">
                      {(registro.tipo === 'entrada' || registro.tipo === 'saldo_enviado') ? '+' : '-'}
                      {formatCurrency(registro.valor)}
                    </td>
                    <td className="px-6 py-4">
                      {registro.status && (
                        <Badge
                          variant={
                            registro.status === 'aprovado'
                              ? 'default'
                              : registro.status === 'pendente'
                                ? 'secondary'
                                : 'destructive'
                          }
                        >
                          {registro.status}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modais */}
      {modal === 'solicitacao' && (
        <SolicitacaoModal onClose={() => setModal(null)} ministerioId={ministerioId} />
      )}
      {modal === 'entrada' && (
        <EntradaModal onClose={() => setModal(null)} ministerioId={ministerioId} />
      )}
      {modal === 'prestacao' && (
        <PrestacaoModal onClose={() => setModal(null)} ministerioId={ministerioId} />
      )}
      {modal === 'transferir' && (
        <TransferenciaModal onClose={() => setModal(null)} ministerioId={ministerioId} />
      )}
    </div>
  )
}

// Page wrapper to read route params
export function FinancasMinisterioDetalhePage() {
  const { ministerioNome } = useParams<{ ministerioNome: string }>()
  
  if (!ministerioNome) {
    return <div>Ministério não encontrado</div>
  }

  return <FinancasMinisterio ministerioNome={ministerioNome} />
}

// Modais de formulário (placeholders)

function SolicitacaoModal({
  onClose,
  ministerioId,
}: {
  onClose: () => void
  ministerioId: string
}) {
  const [titulo, setTitulo] = useState('')
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const [prioridade, setPrioridade] = useState<'baixa' | 'media' | 'alta' | 'urgente'>('media')
  const addSolicitacao = useFinancasStore((s) => s.addSolicitacao)
  const { perfil } = useAuth()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (!titulo || !valor || !motivo || !perfil) return
    setLoading(true)
    addSolicitacao({
      ministerioId,
      titulo,
      motivo,
      valor: Number(valor),
      solicitante: perfil?.nome || 'Usuário',
      prioridade,
    })
    setLoading(false)
    setTitulo('')
    setValor('')
    setMotivo('')
    setPrioridade('media')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg bg-background border shadow-lg p-6">
        <h2 className="mb-4 text-xl font-bold">Solicitação de Verba</h2>
        <Input
          placeholder="Título"
          className="mb-2"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />
        <Input
          placeholder="Valor"
          className="mb-2"
          value={valor}
          onChange={(e) => setValor(e.target.value.replace(/[^0-9.,]/g, ''))}
        />
        <Input
          placeholder="Motivo"
          className="mb-2"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        <div className="mb-4">
          <Label htmlFor="prioridade" className="text-sm font-medium mb-2 block">
            Prioridade
          </Label>
          <Select value={prioridade} onValueChange={(value) => setPrioridade(value as 'baixa' | 'media' | 'alta' | 'urgente')}>
             <SelectTrigger>
               <SelectValue placeholder="Selecione a prioridade" />
             </SelectTrigger>
             <SelectContent>
               <SelectItem value="baixa">Baixa</SelectItem>
               <SelectItem value="media">Média</SelectItem>
               <SelectItem value="alta">Alta</SelectItem>
               <SelectItem value="urgente">Urgente</SelectItem>
             </SelectContent>
           </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !titulo || !valor || !motivo}>
            {loading ? 'Enviando...' : 'Enviar Solicitação'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function EntradaModal({ onClose, ministerioId }: { onClose: () => void; ministerioId: string }) {
  const [tipo, setTipo] = useState<'oferta' | 'cantina' | 'doacao'>('oferta')
  const [valor, setValor] = useState('')
  const [loading, setLoading] = useState(false)
  const { perfil } = useAuth()
  const addEntrada = useFinancasStore((s) => s.addEntrada)

  const handleRegistrar = () => {
    if (!valor || !perfil) return
    setLoading(true)

    addEntrada({
      ministerioId,
      tipo,
      valor: Number(valor.replace(/\./g, '').replace(/,/g, '.')),
      responsavel: perfil?.nome || 'Usuário',
    })

    setTimeout(() => {
      setLoading(false)
      setValor('')
      onClose()
    }, 500)
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg bg-background border shadow-lg p-6">
        <h2 className="mb-4 text-xl font-bold">Registrar Entrada</h2>
        <div className="mb-2">
          <Select value={tipo} onValueChange={(value) => setTipo(value as 'oferta' | 'cantina' | 'doacao')}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="oferta">Oferta</SelectItem>
              <SelectItem value="cantina">Cantina</SelectItem>
              <SelectItem value="doacao">Doação</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Valor"
          className="mb-4"
          value={valor}
          onChange={(e) => setValor(e.target.value.replace(/[^0-9.,]/g, ''))}
        />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleRegistrar} disabled={loading || !valor}>
            {loading ? 'Registrando...' : 'Registrar'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PrestacaoModal({ onClose, ministerioId }: { onClose: () => void; ministerioId: string }) {
  const [titulo, setTitulo] = useState('')
  const [valor, setValor] = useState('')
  const [notaFile, setNotaFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const addPrestacao = useFinancasStore((s) => s.addPrestacao)
  const { perfil } = useAuth()

  const handleSubmit = async () => {
    if (!titulo || !valor || !perfil) return
    setLoading(true)
    await addPrestacao({
      ministerioId,
      titulo,
      valor: Number(valor),
      autor: perfil?.nome || 'Usuário',
      observacoes: '',
      notaFile,
    })
    setLoading(false)
    setTitulo('')
    setValor('')
    setNotaFile(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg bg-background border shadow-lg p-6">
        <h2 className="mb-4 text-xl font-bold">Prestar Contas</h2>
        <Input
          placeholder="Título"
          className="mb-2"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />
        <Input
          placeholder="Valor Total"
          className="mb-2"
          value={valor}
          onChange={(e) => setValor(e.target.value.replace(/[^0-9.,]/g, ''))}
        />
        <input
          type="file"
          accept="image/*"
          className="mb-4"
          onChange={(e) => setNotaFile(e.target.files ? e.target.files[0] : null)}
        />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !titulo || !valor}>
            {loading ? 'Enviando...' : 'Enviar Prestação'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function TransferenciaModal({ onClose, ministerioId }: { onClose: () => void; ministerioId: string }) {
  const [valor, setValor] = useState('')
  const [destino, setDestino] = useState<string | 'igreja'>('igreja')
  const [loading, setLoading] = useState(false)
  const addSolicitacao = useFinancasStore((s) => s.addSolicitacao)
  const { perfil } = useAuth()

  const outras = mockMinisterios.filter((m) => m.id !== ministerioId)

  const handleSubmit = () => {
    if (!valor || !perfil) return
    setLoading(true)
    const destinoName =
      destino === 'igreja' ? 'Saldo da Igreja' : outras.find((o) => o.id === destino)?.nome || 'Outro'
    addSolicitacao({
      ministerioId,
      titulo: `Transferência para ${destinoName}`,
      motivo: `Solicitação de transferência do ministério ${ministerioId} para ${destino === 'igreja' ? 'Saldo da Igreja' : destino}`,
      valor: Number(valor),
      solicitante: perfil?.nome || 'Usuário',
    })

    setTimeout(() => {
      setLoading(false)
      setValor('')
      setDestino('igreja')
      onClose()
    }, 400)
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg bg-background border shadow-lg p-6">
        <h2 className="mb-4 text-xl font-bold">Transferir Valor</h2>
        <Input
          placeholder="Valor"
          className="mb-2"
          value={valor}
          onChange={(e) => setValor(e.target.value.replace(/[^0-9.,]/g, ''))}
        />
        <div className="mb-4">
          <Select value={destino} onValueChange={setDestino}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o destino" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="igreja">Saldo da Igreja</SelectItem>
              {outras.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !valor}>
            {loading ? 'Solicitando...' : 'Solicitar Transferência'}
          </Button>
        </div>
      </div>
    </div>
  )
}